import {
  MAX_PASSWORD_LENGTH,
  NEW_PASSWORD_HINT,
  PASSWORD_STRENGTH_LABEL,
  passwordProblem,
  passwordRequirements,
  passwordStrength,
  type PasswordAbout,
  type PasswordRequirement,
} from '@calling/shared';
import { useEffect, useState, type ReactNode, type Ref } from 'react';
import { joinList } from '../lib/format';
import { CheckIcon, RingIcon } from './icons';

/** How long typing must pause before the strength is read out, so that a screen reader does not speak at every key. */
const SPEAK_AFTER_MS = 1000;

/**
 * The line under the bars while the password is typed: that it misses a
 * requirement, or, once it meets all four, why it would be refused all the
 * same (and so is no better than weak). Short, to stay on one line; the form
 * says it in full when the field is left or the form sent.
 */
const NOTES = {
  unmet: 'Password doesn’t meet the requirements.',
  personal: 'Too close to your name or email address.',
  repetitive: 'It repeats itself, so it is easy to guess.',
  common: 'One of the most common passwords.',
  too_long: `Use ${MAX_PASSWORD_LENGTH} characters or fewer.`,
} as const;
type Note = keyof typeof NOTES;

/** The hint, cut where it names each requirement, so that each can carry its tick. */
function hintParts(): string[] {
  const labels = passwordRequirements('').map((r) => r.label.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
  return NEW_PASSWORD_HINT.split(new RegExp(`(${labels.join('|')})`));
}

/** The hint, with a tick or a ring before each requirement. The comma, "and" or full stop after one stays on its line. */
function hintWithTicks(requirements: PasswordRequirement[]): ReactNode[] {
  const parts = hintParts();
  const pieces: ReactNode[] = [];
  for (let i = 0; i < parts.length; i++) {
    const rule = requirements.find((r) => r.label === parts[i]);
    if (!rule) {
      pieces.push(parts[i]);
      continue;
    }
    const after = parts[i + 1] ?? '';
    const stuck = after.trimEnd();
    pieces.push(
      <span key={rule.id} className="pw-strength__rule" data-met={rule.met} data-testid={`password-rule-${rule.id}`}>
        {rule.met ? <CheckIcon size={13} /> : <RingIcon size={13} />}
        {rule.label}
        {rule.met ? <span className="sr-only"> (done)</span> : null}
        {stuck}
      </span>,
      after.slice(stuck.length),
    );
    i++;
  }
  return pieces;
}

/**
 * What goes under the field for a new password: a bar of four that fills as
 * the password gets stronger, with how strong it is named at its right; a
 * line saying what is wrong while it is typed (or, in its place, `children`:
 * the form's own words for it); and the hint, with a tick by each requirement
 * met. The hint has `hintId`, for the field's aria-describedby. A screen
 * reader hears the strength and what is wrong once typing pauses; the bar and
 * the label beside them are for the eye. `about` is who the password is for
 * (see passwordStrength). `refused`: the form has refused this password, for
 * a reason the meter may not know (the server's longer list of common
 * passwords), so it is no better than weak until it is changed.
 */
export function PasswordStrength({
  password,
  about,
  hintId,
  refused = false,
  ref,
  children,
}: {
  password: string;
  about: PasswordAbout;
  hintId: string;
  refused?: boolean;
  ref?: Ref<HTMLDivElement>;
  children?: ReactNode;
}) {
  const measured = passwordStrength(password, about);
  const strength = refused && (measured === 'good' || measured === 'strong') ? 'weak' : measured;
  const requirements = passwordRequirements(password);
  const missing = requirements.filter((r) => !r.met);
  // Once all four are met, the only problems left are those NOTES names.
  const note: Note | null = !password ? null : missing.length > 0 ? 'unmet' : (passwordProblem(password, about) as Exclude<Note, 'unmet'> | null);
  const label = strength === 'none' ? '' : PASSWORD_STRENGTH_LABEL[strength];
  const why = missing.length > 0 ? ` Still needed: ${joinList(missing.map((r) => r.label), 'and')}.` : note ? ` ${NOTES[note]}` : '';
  const summary = strength === 'none' ? '' : `Password strength: ${label}.${why}`;
  const [spoken, setSpoken] = useState('');

  // Restarted at every key, so the summary is read only once typing pauses, however long the password.
  useEffect(() => {
    const timer = window.setTimeout(() => setSpoken(summary), SPEAK_AFTER_MS);
    return () => window.clearTimeout(timer);
  }, [password, summary]);

  return (
    <div className="pw-strength" data-strength={strength} data-testid="password-strength" ref={ref}>
      <div className="pw-strength__meter" aria-hidden="true">
        <span className="pw-strength__bar">
          <span />
          <span />
          <span />
          <span />
        </span>
        <span className="pw-strength__label" data-testid="password-strength-label">
          {label}
        </span>
      </div>
      <span className="sr-only" role="status" data-testid="password-strength-spoken">
        {spoken}
      </span>
      {/* Every line that can show here is drawn in the same place, the hidden ones too, so that typing never moves what is under it. */}
      <span className="pw-strength__note">
        {(Object.keys(NOTES) as Note[]).map((key) => {
          const shown = !children && key === note;
          return (
            <span key={key} className="error" data-shown={shown} aria-hidden={shown ? undefined : true} data-testid={key === 'unmet' ? 'password-unmet' : `password-note-${key}`}>
              {NOTES[key]}
            </span>
          );
        })}
        {children}
      </span>
      <span className="hint pw-strength__hint" id={hintId} data-testid="password-hint">
        {hintWithTicks(requirements)}
      </span>
    </div>
  );
}
